import { useState } from "react";
import { OptOutConfirmationDialog } from "@/core/components/opt-out-confirmation-dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsContent,
  TabsList,
} from "@volca/ui/components/ui/tabs";
import { toast } from "@volca/ui/lib/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { NoteList } from "@/business-settings/customers/common/note-list";
import { getErrorMessage } from "@/core/api/errors";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATE } from "@/customer/constants";
import {
  useGetCustomerNotes,
  useGetCustomer,
  useOptOutCustomer,
} from "@/customer/query";
import { LeadDetailsForm } from "./lead-details-form";
import { LeadSidebarSkeleton } from "./lead-sidebar-skeleton";

interface LeadSidebarProps {
  businessId: string;
  leadId: string | undefined;
}

const LeadSidebar = ({ businessId, leadId }: LeadSidebarProps) => {
  const { data: lead, isLoading } = useGetCustomer(leadId);

  const [activeTab, setActiveTab] = useState("details");
  const { resetSidebar } = useBusinessSettingsSidebar();
  const optOutCustomer = useOptOutCustomer(businessId);
  const [isOptOutDialogOpen, setIsOptOutDialogOpen] = useState(false);

  const { data: notes = [] } = useGetCustomerNotes(leadId || "");

  const headerText = "Leads";
  const name =
    lead?.name || formatPhoneNumber(lead?.phone_number) || "New lead";

  const handleOptOut = () => {
    if (leadId) {
      optOutCustomer.mutate(leadId, {
        onSuccess: () => {
          toast.success("Customer opted out of messaging");
          setIsOptOutDialogOpen(false);
          resetSidebar();
        },
        onError: (error) => {
          toast.error("Couldn't opt the customer out of messaging", {
            description: getErrorMessage(error),
          });
        },
      });
    }
  };

  const dropdownActions =
    leadId && lead?.state !== CUSTOMER_STATE.opted_out
      ? [
          {
            label: "Opt out of messaging",
            onClick: () => setIsOptOutDialogOpen(true),
            variant: "destructive" as const,
          },
        ]
      : undefined;

  if (isLoading) {
    return <LeadSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
        dropdownActions={dropdownActions}
      />
      <OptOutConfirmationDialog
        open={isOptOutDialogOpen}
        onOpenChange={setIsOptOutDialogOpen}
        customerName={name}
        isPending={optOutCustomer.isPending}
        onConfirm={handleOptOut}
      />
      <div className="flex-1 overflow-hidden">
        <Stack align="start" gap={0} className="h-full w-full">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex w-full flex-1 flex-col gap-0 overflow-hidden px-6"
          >
            <div className="w-full border-b">
              <TabsList variant="line" className="w-full justify-center">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="details"
              className="min-h-0 overflow-y-hidden pt-4"
            >
              <LeadDetailsForm businessId={businessId} initialData={lead} />
            </TabsContent>

            <TabsContent value="notes" className="min-h-0 overflow-y-hidden">
              <NoteList notes={notes} customerId={leadId} />
            </TabsContent>
          </Tabs>
        </Stack>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default LeadSidebar;
